import { el, clear } from './core/dom.js';
import { api } from './core/api.js';
import { withProduct } from './core/route-map.js';
import { currentTheme } from './core/theme.js';
import { createStore } from './core/store.js';
import { renderSidebar } from './components/sidebar.js';
import { renderTopbar } from './components/topbar.js';
import { renderScreen } from './screens/soon.js';
import { renderDashboard } from './screens/dashboard/index.js';
import { renderThread } from './screens/chat/thread.js';
import { renderNewChat } from './screens/new-chat/screen.js';
import { renderKnowledge } from './screens/knowledge/index.js';
import { renderConnectors } from './screens/connectors/index.js';
import { renderSchedules } from './screens/schedules/index.js';
import { renderSettings } from './screens/settings/index.js';
import { renderNewProduct } from './screens/new-product/index.js';
import { renderProductContext } from './screens/product-context/index.js';
import { renderModelDetail } from './screens/settings/model-detail.js';
import { renderNotFound } from './screens/status/not-found.js';
import { offlineBanner, renderOffline } from './screens/status/offline.js';
import { openSearch } from './components/command-k.js';
import { openNotifications } from './components/notifications.js';
import { toast } from './components/toast.js';
import { navigate } from './core/router.js';
import {
  hideWizardSidebar, showWelcomeBadge, wizardCrumb,
} from './screens/new-product/model.js';

const GROUP = new Set(['settings', 'connectors', 'knowledge']);

function isSuper(me) {
  return !!(me && me.role === 'admin' && me.tenant_slug === 'craftisanlabs');
}

function pickProduct(products) {
  const fromUrl = new URLSearchParams(window.location.search).get('product_id') || '';
  if (fromUrl && products.some((item) => item.id === fromUrl)) return fromUrl;
  let stored = '';
  try { stored = localStorage.getItem('selectedProductId') || ''; } catch { /* ignore */ }
  if (stored && products.some((item) => item.id === stored)) return stored;
  return products[0] ? products[0].id : '';
}

function rememberProduct(id) {
  if (!id) return;
  try { localStorage.setItem('selectedProductId', id); } catch { /* ignore */ }
  const url = new URL(window.location.href);
  const path = url.pathname.replace(/\/+$/, '') || '/';
  // Creating a product: leave the URL empty until the wizard saves one.
  if (path === '/app/products/new' && !url.searchParams.get('product_id')) return;
  if (url.searchParams.get('product_id') === id) return;
  url.searchParams.set('product_id', id);
  window.history.replaceState(null, '', `${url.pathname}${url.search}`);
}

/**
 * @param {{ me: object, onTheme: () => void, onLogout: () => void }} opts
 */
export function mountShell(opts) {
  const store = createStore({
    me: opts.me,
    products: [],
    productId: '',
    chats: [],
    chatsStatus: 'loading',
    unread: 0,
    settingsOpen: false,
    productsOpen: false,
    accountOpen: false,
    menuOpen: false,
    view: null,
    wizardCrumb: '',
    wizardWelcome: false,
    wizardStep: 1,
    wizardProductId: '',
  });
  const nodes = mountFrame();
  nodes.scrim.addEventListener('click', () => store.set({ menuOpen: false }));
  store.subscribe(() => paint(store, nodes, opts));
  document.addEventListener('click', (event) => closeMenus(store, event));
  document.addEventListener('keydown', (event) => onShellKey(store, event));
  document.addEventListener('bonnie:wizard-meta', (event) => {
    const detail = (event && event.detail) || {};
    store.set({
      wizardCrumb: detail.crumb || '',
      wizardWelcome: !!detail.welcome,
      wizardStep: detail.step || 1,
      wizardProductId: detail.productId || '',
    });
  });
  wireOffline(store, nodes);
  paint(store, nodes, opts);
  document.addEventListener('bonnie:chats', () => {
    loadChats(store, store.get().productId);
  });
  document.addEventListener('bonnie:products', () => {
    loadProducts(store);
  });
  loadProducts(store);
  loadUnread(store);
  return {
    show(view) {
      const current = store.get();
      store.set({
        view,
        settingsOpen: GROUP.has(view.nav) ? true : current.settingsOpen,
        menuOpen: false,
        productsOpen: false,
        accountOpen: false,
      });
    },
    openSearch() { openSearch({ productId: store.get().productId }); },
    productPath(path) { return withProduct(path, store.get().productId); },
  };
}

function mountFrame() {
  const root = document.getElementById('app');
  const scrim = el('button', {
    type: 'button',
    class: 'nav-scrim',
    'aria-label': 'Close menu',
    hidden: true,
  });
  const sidebarHost = el('div', { class: 'sidebar-host' });
  const topbarHost = el('div', { class: 'topbar-host' });
  const bannerHost = el('div', { class: 'shell-banner-host' });
  const main = el('main', { id: 'main', class: 'main' });
  clear(root);
  root.append(el('div', { class: 'shell' },
    sidebarHost,
    scrim,
    el('div', { class: 'shell-main' }, topbarHost, bannerHost, main)));
  return { scrim, sidebarHost, topbarHost, bannerHost, main };
}

function wireOffline(store, nodes) {
  function sync() {
    clear(nodes.bannerHost);
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      nodes.bannerHost.append(offlineBanner({
        onOpen: () => navigate(withProduct('/app/offline', store.get().productId)),
      }));
    }
  }
  window.addEventListener('offline', sync);
  window.addEventListener('online', sync);
  sync();
}

function closeMenus(store, event) {
  const state = store.get();
  if (!state.productsOpen && !state.accountOpen) return;
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (target.closest('.product-menu') || target.closest('.acct')) return;
  store.set({ productsOpen: false, accountOpen: false });
}

function onShellKey(store, event) {
  const state = store.get();
  if (event.key === 'Escape') {
    if (state.productsOpen || state.accountOpen || state.menuOpen) {
      event.preventDefault();
      store.set({ productsOpen: false, accountOpen: false, menuOpen: false });
    }
    return;
  }
  if (!state.productsOpen) return;
  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Home' && event.key !== 'End') {
    return;
  }
  const list = document.getElementById('product-list');
  if (!list) return;
  const items = [...list.querySelectorAll('.menu-item')];
  if (!items.length) return;
  event.preventDefault();
  const active = document.activeElement;
  let index = items.indexOf(active instanceof Element ? active : null);
  if (event.key === 'Home') index = 0;
  else if (event.key === 'End') index = items.length - 1;
  else if (event.key === 'ArrowDown') index = index < 0 ? 0 : (index + 1) % items.length;
  else index = index < 0 ? items.length - 1 : (index - 1 + items.length) % items.length;
  const next = items[index];
  if (next && typeof next.focus === 'function') next.focus();
}

let screenKey = '';

function releaseScreen(nodes) {
  for (const child of [...nodes.main.children]) {
    child.dispatchEvent(new CustomEvent('bonnie:unmount'));
  }
}

function paint(store, nodes, opts) {
  const state = store.get();
  const view = state.view || {
    id: 'dashboard', title: 'Dashboard', crumb: 'Dashboard', nav: 'dashboard',
    path: '/app/dashboard', params: {}, missing: false, denied: false,
  };
  const product = state.products.find((item) => item.id === state.productId) || null;
  const wizardProduct = wizardDisplayProduct(view, product);
  const wizardUrl = wizardUrlState(view);
  const hideSide = view.id === 'product-new'
    && hideWizardSidebar(state.products, wizardUrl.productId, wizardUrl.step);
  const welcome = view.id === 'product-new'
    && (state.wizardWelcome != null
      ? state.wizardWelcome
      : showWelcomeBadge(state.products, wizardUrl.productId, wizardUrl.step));
  const crumb = view.id === 'product-new'
    ? (state.wizardCrumb || wizardCrumb(wizardUrl.step, wizardUrl.connect) || view.crumb)
    : view.crumb;

  const shellRoot = nodes.sidebarHost.closest('.shell') || document.querySelector('.shell');
  if (shellRoot) shellRoot.classList.toggle('is-onboarding-first', hideSide);

  clear(nodes.sidebarHost);
  if (!hideSide) {
    nodes.sidebarHost.append(renderSidebar({
      nav: view.nav,
      productId: state.productId,
      settingsOpen: state.settingsOpen,
      productName: wizardProduct ? wizardProduct.name : '',
      chats: state.chats,
      chatsStatus: state.chatsStatus,
      chatId: (view.params && view.params.id) || '',
      superAdmin: isSuper(state.me),
      tenantName: state.me.tenant_name,
      plan: state.me.tenant_plan,
    }, {
      onToggleSettings() { store.set({ settingsOpen: !store.get().settingsOpen }); },
    }));
  }
  const aside = nodes.sidebarHost.querySelector('.sidebar');
  if (aside) aside.classList.toggle('is-open', state.menuOpen);
  nodes.scrim.hidden = hideSide || !state.menuOpen;
  clear(nodes.topbarHost);
  nodes.topbarHost.append(renderTopbar({
    crumb,
    product: wizardProduct,
    products: state.products,
    productId: state.productId,
    theme: currentTheme(),
    unread: state.unread,
    me: state.me,
    menuOpen: state.menuOpen,
    productsOpen: state.productsOpen,
    accountOpen: state.accountOpen,
    welcomeBadge: welcome,
    hideNavToggle: hideSide,
    firstProduct: hideSide,
  }, topbarActions(store, opts)));
  if (state.productsOpen) {
    const first = nodes.topbarHost.querySelector('#product-list .menu-item');
    const active = document.activeElement;
    if (first && (!(active instanceof Element) || !active.closest('#product-list'))) {
      setTimeout(() => first.focus(), 0);
    }
  }
  const threadId = view.id === 'chat' && view.params && view.params.id;
  const fresh = view.id === 'chat-new' || (view.id === 'chat' && !threadId);
  const flush = !!(threadId || fresh);
  const key = `${view.id}|${view.path}|${threadId || ''}|${state.productId}|${view.missing}|${view.denied}|${product ? product.name : ''}|${flush}`;
  if (key !== screenKey) {
    releaseScreen(nodes);
    screenKey = key;
    nodes.main.classList.toggle('is-flush', flush);
    clear(nodes.main);
    nodes.main.append(screenFor(view, state, wizardProduct || product, threadId, fresh));
  }
}

function wizardDisplayProduct(view, product) {
  if (!view || view.id !== 'product-new') return product;
  const urlId = new URLSearchParams(window.location.search).get('product_id') || '';
  if (urlId && product && product.id === urlId) return product;
  if (urlId) return product;
  return { name: 'New product' };
}

function wizardUrlState(view) {
  if (!view || view.id !== 'product-new') {
    return { productId: '', step: 1, connect: '' };
  }
  const params = new URLSearchParams(window.location.search);
  const step = Number(params.get('step')) || 1;
  return {
    productId: params.get('product_id') || '',
    step: step >= 1 && step <= 3 ? step : 1,
    connect: params.get('connect') || '',
  };
}

function screenFor(view, state, product, threadId, fresh) {
  if (threadId) {
    return renderThread({
      groupId: view.params.id,
      me: state.me,
      productId: state.productId,
      productName: product ? product.name : '',
      chats: state.chats,
    });
  }
  if (fresh) {
    return renderNewChat({
      me: state.me,
      productId: state.productId,
      productName: product ? product.name : '',
    });
  }
  return renderPage(view, state, product);
}

function renderPage(view, state, product) {
  if (view.id === 'dashboard' && !view.missing && !view.denied) {
    return renderDashboard({
      productId: state.productId,
      productName: product ? product.name : '',
      me: state.me,
    });
  }
  if (view.id === 'knowledge' && !view.missing && !view.denied) {
    return renderKnowledge({
      productId: state.productId,
      productName: product ? product.name : '',
      canEdit: !!(state.me && state.me.role === 'admin'),
      memoryV2: !!(state.me && state.me.memory_v2),
    });
  }
  if (view.id === 'connectors' && !view.missing && !view.denied) {
    return renderConnectors({
      productId: state.productId,
      productName: product ? product.name : '',
      me: state.me,
    });
  }
  if (view.id === 'schedules' && !view.missing && !view.denied) {
    return renderSchedules({
      productId: state.productId,
      productName: product ? product.name : '',
      me: state.me,
    });
  }
  if (view.id === 'settings' && !view.missing && !view.denied) {
    return renderSettings({
      section: (view.params && view.params.section) || 'team',
      productId: state.productId,
      productName: product ? product.name : '',
      me: state.me,
    });
  }
  if (view.id === 'product-new' && !view.missing && !view.denied) {
    return renderNewProduct({
      me: state.me,
      productId: state.productId,
      productName: product ? product.name : '',
      products: state.products || [],
    });
  }
  if (view.id === 'product-context' && !view.missing && !view.denied) {
    return renderProductContext({
      me: state.me,
      productId: state.productId,
      focusProductId: (view.params && view.params.id) || state.productId,
    });
  }
  if (view.id === 'model-detail' && !view.missing && !view.denied) {
    return renderModelDetail({
      me: state.me,
      productId: state.productId,
      modelId: (view.params && view.params.modelId) || '',
    });
  }
  if (view.id === 'offline') {
    return renderOffline({ productId: state.productId });
  }
  if (view.missing) {
    return renderNotFound({ productId: state.productId, path: view.path });
  }
  return renderScreen(view, { productId: state.productId });
}

function topbarActions(store, opts) {
  return {
    onMenu() {
      store.set({ menuOpen: !store.get().menuOpen, productsOpen: false, accountOpen: false });
    },
    onToggleProducts() {
      store.set({ productsOpen: !store.get().productsOpen, accountOpen: false });
    },
    onToggleAccount() {
      store.set({ accountOpen: !store.get().accountOpen, productsOpen: false });
    },
    onProduct(id) { chooseProduct(store, id); },
    onSearch() { openSearch({ productId: store.get().productId }); },
    onNotifications() {
      const state = store.get();
      openNotifications({
        onRead: (unread) => store.set({ unread }),
        productId: state.productId,
        role: state.me && state.me.role,
      });
    },
    onTheme() {
      opts.onTheme();
      store.set({ accountOpen: false });
    },
    onLogout() { opts.onLogout(); },
  };
}

async function chooseProduct(store, id) {
  rememberProduct(id);
  store.set({ productId: id, productsOpen: false, chatsStatus: 'loading' });
  await loadChats(store, id);
}

async function loadProducts(store) {
  const data = await api('/products');
  if (!data || data.error) {
    toast('Could not load products', 'error');
    store.set({ products: [], chatsStatus: 'error' });
    return;
  }
  const products = data.products || [];
  const productId = pickProduct(products);
  rememberProduct(productId);
  store.set({ products, productId });
  await loadChats(store, productId);
}

async function loadChats(store, productId) {
  if (!productId) {
    store.set({ chats: [], chatsStatus: 'ready' });
    return;
  }
  const data = await api(`/chat?product_id=${encodeURIComponent(productId)}`);
  if (!data || data.error) {
    store.set({ chats: [], chatsStatus: 'error' });
    return;
  }
  const chats = (data.groups || []).slice(0, 40).map((group) => ({
    id: group.id,
    name: group.name,
    last_message_at: group.last_message_at,
  }));
  store.set({ chats, chatsStatus: 'ready' });
}

async function loadUnread(store) {
  const data = await api('/notifications');
  if (!data || data.error) return;
  store.set({ unread: data.unread || 0 });
}
